<template>
  <v-navigation-drawer
    :value="value"
    right
    temporary
    fixed
    :width="drawerWidth"
    class="org-person-panel"
    @input="$emit('input', $event)"
  >
    <div v-if="person" class="pa-4">
      <div class="d-flex align-start">
        <div class="flex-grow-1">
          <div class="org-person-panel__name">{{ person.name }}</div>
          <div v-if="person.title" class="org-person-panel__title">{{ person.title }}</div>
        </div>
        <v-btn icon aria-label="Cerrar" @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <div class="d-flex flex-wrap mt-2">
        <v-btn v-if="person.pipedriveUrl" small text :href="person.pipedriveUrl" target="_blank" rel="noopener">
          <v-icon small left>mdi-open-in-new</v-icon>
          Pipedrive
        </v-btn>
        <v-btn v-if="person.linkedinUrl" small text :href="person.linkedinUrl" target="_blank" rel="noopener">
          <v-icon small left>mdi-linkedin</v-icon>
          LinkedIn
        </v-btn>
      </div>

      <v-divider class="my-3" />

      <template v-if="!editing">
        <div class="org-person-panel__grid">
          <div>
            <div class="pw-label">Reporta a</div>
            <div class="pw-value">{{ placed ? (managerName || 'Nadie (cabeza)') : 'Sin ubicar' }}</div>
          </div>
          <div>
            <div class="pw-label">Área</div>
            <div class="pw-value">{{ areaLabel || '—' }}</div>
          </div>
          <div class="org-person-panel__full">
            <div class="pw-label">Email</div>
            <div class="pw-value org-person-panel__break">{{ person.email || '—' }}</div>
          </div>
          <div class="org-person-panel__full">
            <div class="pw-label">Dueño en Pipedrive</div>
            <div class="pw-value">{{ person.ownerName || '—' }}</div>
          </div>
        </div>

        <div class="d-flex flex-column mt-4">
          <v-btn depressed color="primary" class="mb-2" @click="$emit('place', person)">
            {{ placed ? 'Cambiar jefe o área' : 'Ubicar en el árbol' }}
          </v-btn>
          <v-btn outlined class="mb-2" @click="startEdit">Editar datos</v-btn>
          <v-btn v-if="placed" text color="error" @click="confirmRemove = true">Sacar del árbol</v-btn>
        </div>
      </template>

      <v-form v-else ref="form" @submit.prevent="saveEdit">
        <v-text-field
          v-model.trim="form.name"
          label="Nombre"
          :disabled="saving"
          :error-messages="form.name ? [] : ['El nombre no puede quedar vacío']"
          outlined
          dense
        />
        <v-text-field v-model.trim="form.title" label="Cargo" :disabled="saving" outlined dense />
        <v-text-field v-model.trim="form.email" label="Email" type="email" :disabled="saving" outlined dense />
        <v-text-field v-model.trim="form.linkedinUrl" label="LinkedIn" :disabled="saving" outlined dense />
        <div class="d-flex justify-end">
          <v-btn text :disabled="saving" @click="editing = false">Cancelar</v-btn>
          <v-btn color="primary" depressed :loading="saving" :disabled="!form.name" @click="saveEdit">Guardar</v-btn>
        </div>
      </v-form>

      <v-alert v-if="errorText" type="error" dense text class="mt-3 mb-0">{{ errorText }}</v-alert>
    </div>

    <v-dialog v-model="confirmRemove" max-width="400px">
      <v-card v-if="person">
        <v-card-title>¿Sacar del árbol?</v-card-title>
        <v-card-text>
          {{ person.name }} queda en "Sin ubicar". Quienes le reportan pasan a su jefe.
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer />
          <v-btn text :disabled="removing" @click="confirmRemove = false">Cancelar</v-btn>
          <v-btn color="error" depressed :loading="removing" @click="remove">Sacar del árbol</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-navigation-drawer>
</template>

<script>
import ClientOrgChartsService from '../services/client-org-charts.service';
import { errorMessage } from '../utils/org-chart.utils';

export default {
  name: 'OrgChartPersonPanel',
  props: {
    value: { type: Boolean, default: false },
    orgId: { type: [Number, String], required: true },
    person: { type: Object, default: null },
    placed: { type: Boolean, default: false },
    managerName: { type: String, default: '' },
    areas: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      editing: false,
      form: { name: '', title: '', email: '', linkedinUrl: '' },
      saving: false,
      removing: false,
      confirmRemove: false,
      errorText: ''
    };
  },
  computed: {
    drawerWidth() {
      const breakpoint = this.$vuetify && this.$vuetify.breakpoint;
      return breakpoint && breakpoint.xsOnly ? '100%' : 380;
    },
    areaLabel() {
      return (this.person && this.person.area && this.areas[this.person.area]) || '';
    }
  },
  watch: {
    person() {
      this.editing = false;
      this.errorText = '';
    }
  },
  methods: {
    startEdit() {
      this.form = {
        name: this.person.name || '',
        title: this.person.title || '',
        email: this.person.email || '',
        linkedinUrl: this.person.linkedinUrl || ''
      };
      this.errorText = '';
      this.editing = true;
    },
    async saveEdit() {
      if (!this.form.name) return;
      this.saving = true;
      this.errorText = '';
      // El nombre va solo si cambió; el resto se manda siempre, como antes.
      const { name, ...changes } = this.form;
      if (name !== this.person.name) changes.name = name;
      const res = await ClientOrgChartsService.updatePerson(this.person.personId, changes);
      this.saving = false;
      if (res.status !== 'ok') {
        this.errorText = errorMessage(res, 'No pudimos guardar en Pipedrive. Intenta de nuevo.');
        return;
      }
      this.editing = false;
      // Payload: { personId, name?, title, email, linkedinUrl }
      this.$emit('updated', { personId: this.person.personId, ...changes });
    },
    async remove() {
      this.removing = true;
      this.errorText = '';
      const res = await ClientOrgChartsService.removePosition(this.orgId, this.person.personId);
      this.removing = false;
      this.confirmRemove = false;
      if (res.status !== 'ok') {
        this.errorText = errorMessage(res, 'No pudimos sacarla del árbol. Intenta de nuevo.');
        return;
      }
      this.$emit('removed', this.person);
    }
  }
};
</script>

<style scoped>
.org-person-panel__name {
  font-size: 18px;
  font-weight: 600;
  color: var(--pw-text);
  line-height: 1.3;
}

.org-person-panel__title {
  color: var(--pw-text-muted);
}

.org-person-panel__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.org-person-panel__full {
  grid-column: 1 / -1;
}

.org-person-panel__break {
  word-break: break-all;
}
</style>
